import * as React from "react";

import { createOperationUrl } from "../../../navigation/operation-route-parser";
import { t } from "i18next";
import { Batch } from "../../../model/batch";
import { BatchSelect } from "./batch-select";
import { Card, Grid, ThemeProvider } from "@mui/material";
import { Operation } from "../../../model/operation";
import { OperationSelect } from "./operation-select";
import { operationsService } from "../../../services/operations";
import { menuOperationModel } from "../../preparation-menu/preparation-menu-model";
import { operationContext } from "../../../../context/operation-context";
import { VPUITheme } from "../../../../common/theme/vpui-theme";
import { useMatch, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useEffectAsync } from "../../../../common/hooks/use-effect-async";
import { PreparationPageType } from "../../../navigation/preparation-route-props";
import { freshdeskWidgetService } from "../../../services/freshdesk-widget";

import "./style.scss";

interface IOperationBatchSelectorProps {
    notDisplayBatchSelector: boolean;
}

export const OperationBatchSelector = (props: IOperationBatchSelectorProps) => {
    const [operationList, setOperationList] = useState<Operation[]>([]);
    const [batchList, setBatchList] = useState<Batch[]>([]);

    const [selectedOperation, setSelectedOperation] = useState<Operation | undefined>(operationContext.operation);
    const [selectedBatch, setSelectedBatch] = useState<Batch | undefined>(operationContext.operation ? operationContext.batch : undefined);

    const pathMath = useMatch("operations/:sublist/:operationCode?/:batchId?");
    const navigate = useNavigate();

    useEffectAsync(async () => {
        const operations = (await operationsService.loadOperations())
            .sort((a: Operation, b: Operation) => a.code > b.code ? 1 : -1);
        setOperationList(operations);

        freshdeskWidgetService.setOperationCodes(operations.map(operation => operation.code));
    }, []);

    useEffect(() => {
        setSelectedOperation(pathMath?.params.operationCode ?
            operationList.find(o => o.code === pathMath.params.operationCode) : undefined);
    }, [operationList]);

    useEffectAsync(async () => {
        setSelectedOperation(operationList.find(o => o.code === pathMath?.params.operationCode));
        setBatchList(pathMath?.params.operationCode ?
            await operationsService.loadBatches(pathMath?.params.operationCode) : []);
    }, [pathMath?.params.operationCode]);

    useEffectAsync(async () => {
        if (pathMath?.params.operationCode && pathMath?.params.batchId) {
            await menuOperationModel.refreshCounters(
                pathMath?.params.operationCode, Number(pathMath?.params.batchId));
            setSelectedBatch(batchList.find(o => o.id === Number(pathMath.params.batchId)));
        } else {
            menuOperationModel.updateCounters([]);
            setSelectedBatch(undefined);
            operationContext.removeBatch();
        }
    }, [pathMath?.params.batchId, batchList]);

    const handleOperationSelected = async (operation: Operation | null) => {
        const url = createOperationUrl({
            batchId: undefined,
            operationCode: operation?.code,
            page: pathMath?.params.sublist as PreparationPageType | undefined,
        });

        if (operation) {
            setSelectedOperation(operation);
            operationContext.setOperation(operation);
        } else {
            setSelectedOperation(undefined);
            operationContext.removeOperation();
        }

        navigate(url);
    };

    const handleBatchSelected = (batch: Batch | null) => {
        const url = createOperationUrl({
            batchId: batch?.id,
            operationCode: pathMath?.params.operationCode,
            page: pathMath?.params.sublist as PreparationPageType | undefined,
        });

        if (batch) {
            setSelectedBatch(batch);
            operationContext.setBatchId(batch);
        } else {
            setSelectedBatch(undefined);
            operationContext.removeBatch();
        }
        navigate(url);
    };

    return <ThemeProvider theme={VPUITheme}>
        <Card className="batch-selector" elevation={0}>
            <Grid container spacing={3}>
                <Grid item xs={12} md={props.notDisplayBatchSelector ? 12 : 6}
                      data-testid={"operation-select"}>
                    <OperationSelect
                        propertyKey="operation-select"
                        list={operationList.slice()}
                        label={t("components.operationSelector.operation")}
                        placeholder={t("components.operationSelector.placeholder")}
                        noOptionsMessage={t("components.operationSelector.noResults")}
                        onSelect={handleOperationSelected}
                        selectedItem={selectedOperation ?? null}
                    />
                </Grid>
                <Grid item xs={12} md={6} className={props.notDisplayBatchSelector ? "grid-batch-hidden" : ""}
                      data-testid={"batch-select"}>
                    <BatchSelect
                        disabled={selectedOperation === undefined}
                        propertyKey="batch-select"
                        list={batchList}
                        label={t("components.batchSelector.batch")}
                        placeholder={t("components.batchSelector.placeholder")}
                        noOptionsMessage={t("components.batchSelector.noResults")}
                        onSelect={handleBatchSelected}
                        selectedItem={selectedBatch ?? null}
                    />
                </Grid>
            </Grid>
        </Card>
    </ThemeProvider>;
};
